/* One open mailbox, as the page holds it: its worker, its summaries, and
 * EVERYTHING ABOUT WHERE THE READER WAS IN IT. Switching mailboxes and back
 * returns to the same query, scroll position, open message and ticks; that
 * is what makes opening several files one after another feel like one app.
 *
 * No DOM here, so the node tests can drive it with a stand in reader.
 */

import { parseQuery, matches, isFiltering } from "./query.js";
import { groupConversations } from "./threads.js";

export class Mailbox {
  /**
   * @param {string} name       what the sidebar calls it
   * @param {File} file
   * @param {(mailbox, events) => reader} open  makes the worker; a test passes a stand in
   * @param {() => void} changed  called when anything the page shows has moved
   */
  constructor(name, file, open, changed) {
    this.name = name;
    this.file = file;
    this.changed = changed;

    this.items = [];          // summaries by id
    this.byDate = [];         // ids, newest first; rebuilt when summaries arrive
    this.dirtyOrder = false;
    this.indexing = true;
    this.progress = 0;        // 0..1 while reading the file
    this.error = null;

    this.query = parseQuery("");
    this.textHits = new Set(); // ids whose text had every plain term
    this.searching = false;
    this.searchProgress = 0;   // 0..1
    this.newestFirst = true;

    this.checked = new Set();
    this.current = null;       // the id open in the reader
    this.scrollTop = 0;

    this.view = [];            // ids listed, in order: what the list draws
    this.dirtyView = true;

    // Conversations (step 7), the reader's switch, off by default. When on,
    // each listed id stands for a whole conversation: its latest message.
    this.conversations = false;
    this.groups = null;
    this.groupedAt = 0;
    this.dirtyGroups = true;

    this.reader = open(this, {
      onSummaries: (items) => {
        for (const item of items) this.items[item.id] = item;
        this.dirtyOrder = this.dirtyView = this.dirtyGroups = true;
        this.changed();
      },
      onProgress: ({ bytes, total }) => {
        this.progress = total ? bytes / total : 1;
        this.changed();
      },
      onDone: ({ count }) => {
        this.indexing = false;
        this.progress = 1;
        if (count === 0) this.error = "empty";
        this.dirtyView = true;
        if (this.query.terms.length) this.searchText();
        this.changed();
      },
      onError: () => {
        this.indexing = false;
        this.error = "unreadable";
        this.changed();
      },
    });
  }

  get total() {
    return this.byDate.length || this.items.length;
  }

  /** What the list shows now, recomputed only when something it depends on moved. */
  listed() {
    if (this.dirtyOrder) {
      const dated = [];
      const undated = [];
      for (const item of this.items) if (item) (item.date === null ? undated : dated).push(item);
      // Newest first; ties by position in the file. Undated messages last either way.
      dated.sort((a, b) => b.date - a.date || a.id - b.id);
      this.byDate = [...dated, ...undated].map((item) => item.id);
      this.dirtyOrder = false;
      this.dirtyView = true;
    }

    if (this.dirtyView) {
      let ids = this.byDate;
      if (!this.newestFirst) {
        const undatedFrom = ids.findIndex((id) => this.items[id].date === null);
        const dated = undatedFrom < 0 ? ids : ids.slice(0, undatedFrom);
        ids = [...dated].reverse().concat(undatedFrom < 0 ? [] : ids.slice(undatedFrom));
      }
      const messages = isFiltering(this.query)
        ? ids.filter((id) => matches(this.items[id], this.query, this.textHits.has(id)))
        : ids;
      this.view = this.conversations ? this.byConversation(messages) : messages;
      this.dirtyView = false;
    }

    return this.view;
  }

  /**
   * Messages to conversations: one entry per conversation with ANY message
   * listed, standing as its latest message, ordered by that message's date.
   */
  byConversation(messages) {
    const seen = new Set();
    const listed = [];
    for (const id of messages) {
      const conversation = this.conversationOf(id);
      if (seen.has(conversation.key)) continue;
      seen.add(conversation.key);
      listed.push(conversation);
    }
    const dated = listed.filter((c) => c.latestDate !== null);
    const undated = listed.filter((c) => c.latestDate === null);
    dated.sort((a, b) => (this.newestFirst ? b.latestDate - a.latestDate : a.latestDate - b.latestDate) || a.latest - b.latest);
    return [...dated, ...undated].map((c) => c.latest);
  }

  /**
   * The conversation a message belongs to. Regrouped when summaries have
   * arrived, but at most once a second while the file is still being read,
   * so a large file does not regroup on every batch; a message the last
   * grouping has not seen stands alone until the next.
   */
  conversationOf(id) {
    if (!this.groups || (this.dirtyGroups && (!this.indexing || performance.now() - this.groupedAt > 1000))) {
      this.groups = groupConversations(this.items);
      this.groupedAt = performance.now();
      this.dirtyGroups = false;
    }
    const item = this.items[id];
    return this.groups.of.get(id) ?? {
      key: `single-${id}`, members: [id], latest: id, latestDate: item?.date ?? null, count: 1,
      subject: item?.subject ?? "", people: [item?.fromName || item?.fromAddress || ""], attachments: Boolean(item?.maybeAttachments),
    };
  }

  /** The messages a listed row stands for: one, or its whole conversation. */
  membersOf(id) {
    return this.conversations ? this.conversationOf(id).members : [id];
  }

  /** Did this one message match the plain words of the search, by its headers or its text? */
  matchesSearch(id) {
    const item = this.items[id];
    return Boolean(item) && this.query.terms.length > 0 && matches(item, { ...this.query, fields: [], attachment: false, before: null, after: null }, this.textHits.has(id));
  }

  setConversations(on) {
    this.conversations = on;
    this.dirtyView = true;
    this.changed();
  }

  /** A row's tick: every message it stands for ticked, some, or none. */
  tickState(id) {
    const members = this.membersOf(id);
    const ticked = members.filter((m) => this.checked.has(m)).length;
    return ticked === 0 ? "none" : ticked === members.length ? "all" : "some";
  }

  isFiltering() {
    return isFiltering(this.query);
  }

  /** A new search box value: header matches at once, text matches as the worker finds them. */
  setQuery(text) {
    if (text === this.query.text) return;
    this.query = parseQuery(text);
    this.textHits = new Set();
    this.dirtyView = true;
    this.searchText();
    this.changed();
  }

  searchText() {
    const terms = this.query.terms.join(" ");
    this.searchProgress = 0;

    if (!terms || this.indexing) {
      this.searching = false;
      if (!terms) this.reader.find("");
      return;
    }

    this.searching = true;
    this.reader.find(
      terms,
      ({ hits, done, total }) => {
        for (const id of hits) this.textHits.add(id);
        this.searchProgress = total ? done / total : 1;
        if (hits.length) this.dirtyView = true;
        this.changed();
      },
      () => {
        this.searching = false;
        this.searchProgress = 1;
        this.changed();
      });
  }

  setNewestFirst(value) {
    this.newestFirst = value;
    this.dirtyView = true;
    this.changed();
  }

  /** Ticks or unticks what a row stands for: a message, or a whole conversation. */
  toggle(id) {
    const tick = this.tickState(id) !== "all";
    for (const member of this.membersOf(id)) {
      if (tick) this.checked.add(member);
      else this.checked.delete(member);
    }
    this.changed();
  }

  /** Tick or untick everything the list shows now; ticks outside it are kept. */
  setAllListed(ticked) {
    for (const id of this.listed()) {
      for (const member of this.membersOf(id)) {
        if (ticked) this.checked.add(member);
        else this.checked.delete(member);
      }
    }
    this.changed();
  }

  clearChecked() {
    this.checked.clear();
    this.changed();
  }

  /** Ticked messages in the order the list shows them, then any the filter hides. */
  checkedInOrder() {
    // In conversation mode a row stands for its members, oldest first.
    const shown = this.listed().flatMap((id) => this.membersOf(id)).filter((id) => this.checked.has(id));
    const inList = new Set(shown);
    const hidden = [...this.checked].filter((id) => !inList.has(id)).sort((a, b) => a - b);
    return [...shown, ...hidden];
  }

  close() {
    this.reader.close();
  }
}
